import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderLayout from '../common/HeaderLayout';
import { languageData } from './languageData';
import { languageMapping } from './languageMapping';
import Select from "react-select";

const UserDataPage = () => {
  const [state, setState] = useState('');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [states, setStates] = useState([]);
  const [language, setLanguage] = useState('english');
  const navigate = useNavigate();

  // ⭐ NEW: Counters
  const [totalUsers, setTotalUsers] = useState(0);
  const [usersWithMudat, setUsersWithMudat] = useState(0);
  const [usersWithoutMudat, setUsersWithoutMudat] = useState(0);

  // ⭐ LAST MUDAT STORAGE
  const [attendanceMap, setAttendanceMap] = useState({});

  // FILTER STATES
  const [pincodeFilter, setPincodeFilter] = useState('');
  const [mobileFilter, setMobileFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [firstNameFilter, setFirstNameFilter] = useState('');
  const [lastNameFilter, setLastNameFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [tagFilter, setTagFilter] = useState(''); // Sevak or Guest

  // OPTIONS
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [mobileOptions, setMobileOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [firstNameOptions, setFirstNameOptions] = useState([]);
  const [lastNameOptions, setLastNameOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);

  // ⭐ FETCH MUDAT
  const fetchAttendance = async (userId) => {
    try {
      const res = await fetch(`https://api.rangpursadhima.cloud/api/attendance/${userId}`);
      if (!res.ok) return null;
      const data = await res.json();
      return data?.lastMarked || null;
    } catch {
      return null;
    }
  };

  const fetchStates = async () => {
    try {
      const res = await fetch('https://api.rangpursadhima.cloud/api/users/states');
      const data = await res.json();
      const validStates = data.filter(
        (stateItem) => stateItem.STATE && stateItem.STATE.trim() !== ''
      );
      setStates(validStates);
    } catch (err) {
      console.error('Failed to fetch states', err);
    }
  };

  const fetchUserData = async () => {
    if (!state) return;

    try {
      const encodedState = encodeURIComponent(state);
      const res = await fetch(
        `https://api.rangpursadhima.cloud/api/users/state?state=${encodedState}`
      );
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

      const data = await res.json();
      setUsers(data);
      setFilteredUsers(data);

      // BUILD FILTER OPTIONS (unchanged)
      const rawP = data.map((u) => (u.PIN_CODE != null ? String(u.PIN_CODE).trim() : ''));
      const hasBlankP = rawP.some((v) => v === '');
      const uniqueP = [...new Set(rawP.filter((v) => v !== ''))];
      setPincodeOptions(hasBlankP ? ['No Pincode', ...uniqueP] : uniqueP);

      const rawM = data.map((u) => (u.MOBILE_NO != null ? String(u.MOBILE_NO).trim() : ''));
      const hasBlankM = rawM.some((v) => v === '');
      const uniqueM = [...new Set(rawM.filter((v) => v !== ''))];
      setMobileOptions(hasBlankM ? ['No Mobile', ...uniqueM] : uniqueM);

      const rawC = data.map((u) => (u.CITY_AREA_VILLAGE != null ? String(u.CITY_AREA_VILLAGE).trim() : ''));
      const hasBlankC = rawC.some((v) => v === '');
      const uniqueC = [...new Set(rawC.filter((v) => v !== ''))];
      setCityOptions(hasBlankC ? ['No City', ...uniqueC] : uniqueC);

      const rawF = data.map((u) => (u.FIRST_NAME != null ? String(u.FIRST_NAME).trim() : ''));
      const hasBlankF = rawF.some((v) => v === '');
      const uniqueF = [...new Set(rawF.filter((v) => v !== ''))];
      setFirstNameOptions(hasBlankF ? ['No First Name', ...uniqueF] : uniqueF);

      const rawL = data.map((u) => (u.LAST_NAME != null ? String(u.LAST_NAME).trim() : ''));
      const hasBlankL = rawL.some((v) => v === '');
      const uniqueL = [...new Set(rawL.filter((v) => v !== ''))];
      setLastNameOptions(hasBlankL ? ['No Last Name', ...uniqueL] : uniqueL);

      const rawD = data.map((u) => (u.DISTRICT != null ? String(u.DISTRICT).trim() : ''));
      const hasBlankD = rawD.some((v) => v === '');
      const uniqueD = [...new Set(rawD.filter((v) => v !== ''))];
      setDistrictOptions(hasBlankD ? ['No District', ...uniqueD] : uniqueD);

      // ⭐ NEW — FETCH LAST MUDAT FOR EACH USER
      const mudatTemp = {};
      for (const u of data) {
        if (u.id) {
          mudatTemp[u.id] = await fetchAttendance(u.id);
        }
      }
      setAttendanceMap(mudatTemp);

      // ⭐ COUNTERS
      setTotalUsers(data.length);

      let withCount = 0;
      let withoutCount = 0;

      for (const u of data) {
        const m = mudatTemp[u.id];
        if (m) withCount++;
        else withoutCount++;
      }

      setUsersWithMudat(withCount);
      setUsersWithoutMudat(withoutCount);

    } catch (err) {
      console.error('Failed to fetch user data', err);
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    fetchUserData();
    setPincodeFilter('');
    setMobileFilter('');
    setCityFilter('');
    setFirstNameFilter('');
    setLastNameFilter('');
    setDistrictFilter('');
    setTagFilter('');
  }, [state]);

  // FILTER LOGIC (unchanged)
  useEffect(() => {
    let result = users;

    // Tag filter (Sevak/Guest)
    if (tagFilter) {
      result = result.filter((u) => (u.tag || 'Sevak') === tagFilter);
    }

    if (pincodeFilter) {
      result = pincodeFilter === 'No Pincode'
        ? result.filter((u) => !u.PIN_CODE || String(u.PIN_CODE).trim() === '')
        : result.filter((u) => String(u.PIN_CODE).trim() === pincodeFilter);
    }

    if (mobileFilter) {
      result = mobileFilter === 'No Mobile'
        ? result.filter((u) => !u.MOBILE_NO || String(u.MOBILE_NO).trim() === '')
        : result.filter((u) => String(u.MOBILE_NO).trim() === mobileFilter);
    }

    if (cityFilter) {
      result = cityFilter === 'No City'
        ? result.filter((u) => !u.CITY_AREA_VILLAGE || String(u.CITY_AREA_VILLAGE).trim() === '')
        : result.filter((u) => u.CITY_AREA_VILLAGE?.toLowerCase().trim() === cityFilter.toLowerCase().trim());
    }

    if (firstNameFilter) {
      result = firstNameFilter === 'No First Name'
        ? result.filter((u) => !u.FIRST_NAME || String(u.FIRST_NAME).trim() === '')
        : result.filter((u) => u.FIRST_NAME?.toLowerCase().trim() === firstNameFilter.toLowerCase().trim());
    }

    if (lastNameFilter) {
      result = lastNameFilter === 'No Last Name'
        ? result.filter((u) => !u.LAST_NAME || String(u.LAST_NAME).trim() === '')
        : result.filter((u) => u.LAST_NAME?.toLowerCase().trim() === lastNameFilter.toLowerCase().trim());
    }

    if (districtFilter) {
      result = districtFilter === 'No District'
        ? result.filter((u) => !u.DISTRICT || String(u.DISTRICT).trim() === '')
        : result.filter((u) => u.DISTRICT?.toLowerCase().trim() === districtFilter.toLowerCase().trim());
    }

    setFilteredUsers(result);
  }, [
    tagFilter,
    pincodeFilter,
    mobileFilter,
    cityFilter,
    firstNameFilter,
    lastNameFilter,
    districtFilter,
    users
  ]);

  const handleStateChange = (selectedState) => {
    setState(selectedState);

    if (languageMapping.gujarati.includes(selectedState)) {
      setLanguage('gujarati');
    } else if (languageMapping.hindi.includes(selectedState)) {
      setLanguage('hindi');
    } else {
      setLanguage('english');
    }
  };

  const handlePrint = () => {
    if (filteredUsers.length > 0) {
      localStorage.setItem('printUsers', JSON.stringify(filteredUsers));
      localStorage.setItem('language', language);
      navigate('/print-page');
    } else {
      alert('No users found for the selected filters!');
    }
  };

  return (
    <HeaderLayout>
      <div className="container mx-auto px-4 py-6">
        <h2 className="text-2xl font-bold mb-6 text-center sm:text-left">
          Select State to Fetch Users
        </h2>

        {/* STATE SELECT + PRINT BUTTON */}
        <div className="flex flex-col sm:flex-row sm:space-x-4 gap-4 mb-6">
          <select
            onChange={(e) => handleStateChange(e.target.value)}
            value={state}
            className="p-3 border border-gray-300 rounded-md w-full sm:w-1/3"
          >
            <option value="">Select State</option>
            {states.map((stateItem, index) => (
              <option key={index} value={stateItem.STATE}>
                {stateItem.STATE}
              </option>
            ))}
          </select>

          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-blue-500 text-white rounded-md shadow-md hover:bg-blue-600 w-full sm:w-auto"
          >
            Print Users
          </button>
        </div>

        {/* ⭐ NEW — USER SUMMARY COUNTERS */}
        {users.length > 0 && (
          <div className="mb-6 p-4 bg-gray-100 rounded-md shadow-sm border border-gray-300">
            <h3 className="text-lg font-semibold mb-2">User Summary</h3>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm font-bold text-gray-700">
              <div className="p-3 bg-white rounded shadow border border-gray-200">
                Total: {totalUsers}
              </div>

              <div className="p-3 bg-purple-50 rounded shadow border border-purple-300">
                Sevak: {users.filter(u => (u.tag || 'Sevak') === 'Sevak').length}
              </div>

              <div className="p-3 bg-orange-50 rounded shadow border border-orange-300">
                Guest: {users.filter(u => u.tag === 'Guest').length}
              </div>

              <div className="p-3 bg-green-50 rounded shadow border border-green-300">
                With Mudat: {usersWithMudat}
              </div>

              <div className="p-3 bg-red-50 rounded shadow border border-red-300">
                Without Mudat: {usersWithoutMudat}
              </div>
            </div>
          </div>
        )}

        {/* FILTERS UI */}
        {users.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">

            {/* Tag Filter (Sevak/Guest) - First */}
            <Select
              options={[
                { label: "Sevak", value: "Sevak" },
                { label: "Guest", value: "Guest" }
              ]}
              value={tagFilter ? { label: tagFilter, value: tagFilter } : null}
              onChange={(e) => setTagFilter(e?.value || "")}
              placeholder="Filter by Tag (Sevak/Guest)"
              isSearchable={false}
              isClearable
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: tagFilter === 'Sevak' ? '#9333ea' : tagFilter === 'Guest' ? '#ea580c' : base.borderColor,
                  borderWidth: tagFilter ? '2px' : '1px',
                })
              }}
            />

            <Select
              options={[
                ...pincodeOptions.map((p) => ({ label: p, value: p })),
                { label: "No Pincode", value: "No Pincode" }
              ]}
              value={pincodeFilter ? { label: pincodeFilter, value: pincodeFilter } : null}
              onChange={(e) => setPincodeFilter(e?.value || "")}
              placeholder="Filter by Pincode"
              isSearchable
              isClearable
            />

            <Select
              options={[
                ...mobileOptions.map((m) => ({ label: m, value: m })),
                { label: "No Mobile", value: "No Mobile" }
              ]}
              value={mobileFilter ? { label: mobileFilter, value: mobileFilter } : null}
              onChange={(e) => setMobileFilter(e?.value || "")}
              placeholder="Filter by Mobile"
              isSearchable
              isClearable
            />

            <Select
              options={[
                ...cityOptions.map((c) => ({ label: c, value: c })),
                { label: "No City", value: "No City" }
              ]}
              value={cityFilter ? { label: cityFilter, value: cityFilter } : null}
              onChange={(e) => setCityFilter(e?.value || "")}
              placeholder="Filter by City"
              isSearchable
              isClearable
            />

            <Select
              options={[
                ...firstNameOptions.map((fn) => ({ label: fn, value: fn })),
                { label: "No First Name", value: "No First Name" }
              ]}
              value={firstNameFilter ? { label: firstNameFilter, value: firstNameFilter } : null}
              onChange={(e) => setFirstNameFilter(e?.value || "")}
              placeholder="Filter by First Name"
              isSearchable
              isClearable
            />

            <Select
              options={[
                ...lastNameOptions.map((ln) => ({ label: ln, value: ln })),
                { label: "No Last Name", value: "No Last Name" }
              ]}
              value={lastNameFilter ? { label: lastNameFilter, value: lastNameFilter } : null}
              onChange={(e) => setLastNameFilter(e?.value || "")}
              placeholder="Filter by Last Name"
              isSearchable
              isClearable
            />

            <Select
              options={[
                ...districtOptions.map((d) => ({ label: d, value: d })),
                { label: "No District", value: "No District" }
              ]}
              value={districtFilter ? { label: districtFilter, value: districtFilter } : null}
              onChange={(e) => setDistrictFilter(e?.value || "")}
              placeholder="Filter by District"
              isSearchable
              isClearable
            />

          </div>
        )}

        {/* USER CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUsers.length > 0 ? (

            filteredUsers.map((user) => {
              const mudat = attendanceMap[user.id];
              const mudatFormatted = mudat ? new Date(mudat).toLocaleDateString() : "0";

              return (
                <div
                  key={user.Sevakcode}
                  className="bg-white p-5 rounded-lg shadow border border-gray-200 text-[14px] leading-snug"
                >
                  <div className="mb-3">
                    <div className="flex justify-between items-start">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {user.FIRST_NAME} {user.LAST_NAME}
                      </h4>
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                        user.tag === 'Guest' 
                          ? 'bg-orange-100 text-orange-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {user.tag === 'Guest' ? 'GUEST' : 'SEVAK'}
                      </span>
                    </div>

                    {/* ⭐ Sevakcode + Last Mudat inline */}
                    <div className="flex justify-between text-sm text-gray-700 font-bold">
                      <span>Sevakcode: {user.Sevakcode}</span>
                      <span>Last Mudat: {mudatFormatted}</span>
                    </div>
                  </div>

                  <hr className="border-t border-gray-300 my-2" />

                  <div className="space-y-1 text-gray-700">

                    <p>
                      <strong>{languageData[language].address}:</strong>{' '}
                      {user.ADDRESS}
                    </p>

                    <div className="flex justify-between gap-2">
                      <p>
                        <strong>{languageData[language].city}:</strong>{' '}
                        {user.CITY_AREA_VILLAGE}
                      </p>
                      <p>
                        <strong>{languageData[language].district}:</strong>{' '}
                        {user.DISTRICT}
                      </p>
                    </div>

                    <div className="flex justify-between gap-2">
                      <p>
                        <strong>{languageData[language].state}:</strong>{' '}
                        {user.STATE}
                      </p>
                      <p>
                        <strong>{languageData[language].taluka}:</strong>{' '}
                        {user.TALUKA}
                      </p>
                    </div>

                    <div className="flex justify-between gap-2">
                      <p>
                        <strong>{languageData[language].pincode}:</strong>{' '}
                        {user.PIN_CODE}
                      </p>
                      <p>
                        <strong>{languageData[language].mobile}:</strong>{' '}
                        {user.MOBILE_NO}
                      </p>
                    </div>

                    {/* ⭐ NEW CONTACT NO */}
                    <div className="flex justify-between gap-2">
                      <p>
                        <strong>Contact No:</strong> {user.CONTACT_NO || "0"}
                      </p>
                    </div>

                  </div>
                </div>
              );
            })

          ) : (
            <p className="text-gray-600">No users found for the selected state.</p>
          )}
        </div>
      </div>
    </HeaderLayout>
  );
};

export default UserDataPage;
